import type {
    QuizProps,
    Step,
    Segment,
    Block,
    Theme,
    SegmentResultBlock,
    MultipleChoiceOption,
} from "./types";

import Quiz from "./Quiz.svelte";
import { Sequence } from "./sequence";

function hexToRgb(hex: string) {
    hex = hex.replace(/^#/, "");

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return { r, g, b };
}

function camelCaseToCssVariable(camelCaseString: string) {
    return (
        "--" +
        camelCaseString.replace(
            /([A-Z])/g,
            (match) => "-" + match.toLowerCase(),
        )
    );
}

/**
 * All this function does is convert empty strings to undefined. Because of how
 * props are passed to the quiz, optional props that are not present come as an
 * empty string.
 * */
function parsePropValue(propValue: any) {
    if (typeof propValue !== "string") {
        return propValue;
    }
    if (propValue.length === 0) {
        return;
    }
    return propValue;
}

function getBlockSetting(settings: Block["settings"], key: string) {
    if (!(key in settings)) {
        return;
    }
    return parsePropValue(settings[key]);
}

function createSegmentResultStep(block: SegmentResultBlock) {
    const subtitleText = getBlockSetting(block.settings, "subtitle");
    let subtitle: { text: string; position: string } | undefined;
    if (subtitleText != null) {
        subtitle = {
            text: subtitleText,
            position: block.settings.subtitle_position,
        };
    }

    return {
        type: block.type,
        title: block.settings.title,
        segmentId: block.settings.segment_id,
        subtitle,
        continueButton: {
            text: block.settings.continue_button_text,
        },
        defaultFormInputType: block.settings.default_form_input_type,
    };
}

function createResultStep(block: Block) {
    return {
        type: block.type,
        title: block.settings.title,
        buyButton: {
            text: block.settings.buy_button_text,
        },
    };
}

function createLoadingStep(block: Block) {
    return {
        type: block.type,
        message: block.settings.message,
        durationInSeconds: block.settings.duration,
    };
}

function createIntroStep(block: Block) {
    return {
        type: block.type,
        title: block.settings.title,
        subtitle: block.settings.subtitle,
        footer: block.settings.footer,
        ctaButton: {
            text: block.settings.cta_button_text,
            backgroundColor: block.settings.cta_button_bg_color,
            textColor: block.settings.cta_button_text_color,
        },
    };
}

function validateEducationalStep(block: Block) {
    if (block.settings.video != null && block.settings.image != null) {
        alert("Error: Educational step cannot have both video and image");
    }
}

function createEducationalStep(block: Block) {
    validateEducationalStep(block);
    return {
        type: block.type,
        title: block.settings.title,
        text: block.settings.text,
        waitUntilCanProceed: block.settings.wait_until_can_proceed,
        video: block.settings.video,
        image: block.settings.image,
        order: block.settings.order,
        requireWatchVideo: block.settings.require_watch_video,
    };
}

function validateMultipleChoiceOptionType(
    optionType: string,
    style: string,
    bgImgSrc: string | undefined,
    iconSrc: string | undefined,
) {
    let errMsg = "";
    switch (optionType) {
        case "text_only":
            if (bgImgSrc != null) {
                errMsg = "- A text-only option cannot have a background image.";
            } else if (iconSrc != null) {
                errMsg = "- A text-only option cannot have an icon.";
            }
            break;

        case "background_image":
            if (iconSrc != null) {
                errMsg = "- A background image option cannot have an icon.";
            }
            if (style === "stack") {
                errMsg = `${errMsg}\n- Stacked style can't be used with background image.`;
            }
            break;

        case "icon":
            if (bgImgSrc != null) {
                errMsg = "- An icon option cannot have a background image.";
            }
            break;

        default:
            break;
    }

    if (errMsg.length > 0) {
        const alertMsg = `Error\n[Multiple Choice Step]\n${errMsg}`;
        alert(alertMsg);
        throw new Error(alertMsg);
    }
}

function createMultipleChoiceStep(block: Block) {
    const optionType = block.settings.option_type;
    const style = block.settings.style;
    const options: MultipleChoiceOption[] = [];
    for (let i = 1; i <= 4; i++) {
        const titleKey = `option_${i}_title`;
        const backgroundImageSrcKey = `option_${i}_background_image`;
        const iconSrcKey = `option_${i}_icon`;
        const answerIdKey = `option_${i}_answer_id`;

        const title = getBlockSetting(block.settings, titleKey);
        if (title == null) {
            continue;
        }

        const answerId = getBlockSetting(block.settings, answerIdKey);
        const backgroundImageSrc = getBlockSetting(
            block.settings,
            backgroundImageSrcKey,
        );
        const iconSrc = getBlockSetting(block.settings, iconSrcKey);

        validateMultipleChoiceOptionType(
            optionType,
            style,
            backgroundImageSrc,
            iconSrc,
        );

        options.push({
            title,
            backgroundImageSrc,
            iconSrc,
            answerId,
        });
    }

    return {
        type: block.type,
        title: block.settings.title,
        style,
        optionType,
        options,
    };
}

function createSteps(blocks: QuizProps["blocks"]) {
    const fixedSteps: Step[] = [];
    const steps: Step[] = [];

    for (const block of blocks) {
        let step;
        let fixedStep;

        switch (block.type) {
            case "intro":
                fixedStep = createIntroStep(block);
                break;
            case "multiple_choice":
                step = createMultipleChoiceStep(block);
                break;
            case "educational":
                step = createEducationalStep(block);
                break;
            case "loading":
                step = createLoadingStep(block);
                break;
            case "result":
                step = createResultStep(block);
                break;
            case "segment_result":
                step = createSegmentResultStep(block);
                break;
            default:
                continue;
        }

        switch (true) {
            case fixedStep != null:
                fixedSteps.push(fixedStep);
                break;
            case step != null:
                steps.push(step);
                break;
            default:
                continue;
        }
    }

    return { fixedSteps, steps };
}

function createSegments(steps: Step[], fixedSteps: Step[]) {
    const segments: Segment[] = [];
    let currSteps: Step[] = [];

    for (const step of fixedSteps) {
        currSteps.push(step);
    }

    segments.push({
        id: "fixed",
        steps: new Sequence(currSteps),
    });
    currSteps = [];

    for (const step of steps) {
        if (step.type === "intro") {
            continue;
        }

        currSteps.push(step);

        if (step.type === "segment_result") {
            if (!step.segmentId?.length) {
                throw new Error(
                    "[shpln-quiz] segmentId missing for segment_result step",
                );
            }

            segments.push({
                id: step.segmentId,
                steps: new Sequence(currSteps),
            });
            currSteps = [];
        }
    }

    return segments;
}

function validateKlaviyoConfig(props: QuizProps) {
    const klaviyoConfig = props.klaviyoConfig;
    if (klaviyoConfig == null) {
        return;
    }

    const listId = parsePropValue(klaviyoConfig.listId);
    if (listId != null) {
        return;
    }

    alert("Error\n[Klaviyo Config] Klaviyo List ID is missing.");
}

function validateProps(props: QuizProps) {
    switch (true) {
        case props.config == null || Object.keys(props.config).length === 0:
            console.error(
                "[shpln-quiz] Config is missing or incomplete: ",
                props.config,
            );
    }
}

class QuizElement extends HTMLElement {
    private props: QuizProps;
    private quiz?: Quiz;
    private isCssLoaded = false;

    constructor() {
        super();

        if (!this.shadowRoot) {
            this.attachShadow({
                mode: "open",
            });
        }

        const propsJson = this.dataset.props;
        let props;
        try {
            props = JSON.parse(propsJson!);
        } catch (err) {
            console.error("[shpln-quiz] Unable to parse props.", err);
        }

        validateProps(props);
        validateKlaviyoConfig(props);

        this.props = props;
        this.setupCSS(props);
        this.setupTheme(props.theme);
    }

    connectedCallback() {
        this.init();
    }

    private onCssLoaded() {
        this.isCssLoaded = true;
        this.quiz?.setReady(true);
    }

    private setupCSS(props: QuizProps) {
        const sectionContainer = document.querySelector(
            `#${props.shopifySectionId}`,
        );
        const sectionCustomStyle =
            sectionContainer?.querySelector("style:last-of-type");

        if (sectionCustomStyle != null) {
            const sectionCustomCss = sectionCustomStyle.innerHTML;
            const shadowCustomStyle = document.createElement("style");
            const shadowCustomCss = sectionCustomCss.replace(
                `#${props.shopifySectionId}`,
                "",
            );
            shadowCustomStyle.innerHTML = shadowCustomCss;
            this.shadowRoot?.prepend(shadowCustomStyle);
        }

        const linkTags: HTMLLinkElement[] = [];
        try {
            const cssSourcesJson = this.dataset.cssSources;
            const cssSources = JSON.parse(cssSourcesJson ?? "[]");

            // Loop backwards because we are prepending each item. This way we can
            // preserve the order of CSS imports.
            for (let i = cssSources.length - 1; i >= 0; i--) {
                const source = cssSources[i];
                const link = document.createElement("link");
                link.rel = "stylesheet";
                link.href = source;
                this.shadowRoot?.prepend(link);
                linkTags.push(link);
            }
        } catch {
            console.error(
                "[sphpln-quiz] css-sources data attribute is missing",
            );
        }

        let semaphore = linkTags.length;
        for (const linkTag of linkTags) {
            linkTag.addEventListener("load", () => {
                --semaphore;
                if (semaphore === 0) {
                    this.onCssLoaded();
                }
            });
        }
    }

    private setupTheme(theme: Theme) {
        const values = [];
        for (const [key, value] of Object.entries(theme)) {
            const varName = camelCaseToCssVariable(key);
            switch (true) {
                case value.startsWith("#"):
                    const { r, g, b } = hexToRgb(value);
                    values.push(`${varName}-rgb: ${r} ${g} ${b};`);
                default:
                    values.push(`${varName}: ${value};`);
            }
        }

        const style = document.createElement("style");
        style.textContent = `:host{${values.join(" ")}}`;
        this.shadowRoot?.prepend(style);
    }

    private init() {
        if (this.shadowRoot == null) {
            console.error(
                "[shpln-quiz] Cannot initialize quiz because shadow root was not found",
            );
            return;
        }

        if (this.props == null) {
            console.error(
                "[shpln-quiz] Cannot initialize quiz because props were not found",
            );
            return;
        }

        if (this.props.presentationStyle === "popup") {
            this.setupPopup();
        }

        const introBlock = this.props.blocks.find((b) => b.type === "intro");

        if (introBlock == null) {
            throw new Error("[shpln-quiz]: Intro block was not found");
        }

        const { fixedSteps, steps } = createSteps(this.props.blocks);
        const segments = createSegments(steps, fixedSteps);
        const config = {
            ...this.props.config,
            privacyPolicyUrl: this.props.privacyPolicyUrl,
        };

        this.quiz = new Quiz({
            target: this.shadowRoot,
            props: {
                ready: this.isCssLoaded,
                backgroundImage: this.props.backgroundImage,
                config,
                coverImage: this.props.coverImage,
                logo: this.props.logo,
                segments,
                klaviyoConfig: this.props.klaviyoConfig,
            },
        });
    }

    private get wrapper() {
        const wrapper = document.querySelector(
            `#${this.props.shopifySectionId} .shpln-quiz-wrapper`,
        );
        if (wrapper == null) {
            console.error("[shpln-quiz] Error: .shpln-quiz-wrapper not found");
            return;
        }
        return wrapper;
    }

    public presentAsPopup() {
        window.requestAnimationFrame(() => {
            this.wrapper?.setAttribute("visible", "totally");
        });
    }

    private closePopup() {
        window.requestAnimationFrame(() => {
            this.wrapper?.removeAttribute("visible");
        });
    }

    private setupPopup() {
        const closeButton = this.wrapper?.querySelector(
            ".shpln-quiz-close-button",
        );
        closeButton?.addEventListener("click", () => {
            this.closePopup();
        });

        const delay = this.props.popupConfig.delayInSeconds * 1000;
        // If the delay is zero, this means that the popup should be invoked
        // directly rather than appearig after some time.
        if (delay === 0) {
            return;
        }

        setTimeout(() => {
            this.presentAsPopup();
        }, delay);
    }
}

if (customElements.get("shpln-quiz") == null) {
    customElements.define("shpln-quiz", QuizElement);
}
